/* eslint-disable react-hooks/exhaustive-deps */
import {
  makeStyles,
  tokens,
  Switch,
  Image,
  Link,
  Text,
  Menu,
  MenuTrigger,
} from "@fluentui/react-components";
import {
  WeatherMoonRegular,
  WeatherSunnyRegular,
  Person24Regular,
  Navigation24Regular,
} from "@fluentui/react-icons";
import useConfigStore from "../../store/config";
import { useMsal } from "@azure/msal-react";
import { useEffect, useMemo } from "react";
import { DataService } from "../../services/dataService";

const useStyles = makeStyles({
  header: {
    padding: 0,
    backgroundColor: "#768692",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    paddingTop: "8px",
    paddingBottom: "8px",
    height: "70px",
    width: "100%",
    "@media (max-width: 768px)": {
      height: "60px",
    },
  },
  container: {
    display: "flex",
    alignItems: "center",
    height: "64px",
    padding: `0 ${tokens.spacingHorizontalL}`,
    justifyContent: "space-between",
    backgroundColor: "#768692",
    width: "100%",
    "@media (max-width: 768px)": {
      height: "54px",
      padding: `0 ${tokens.spacingHorizontalM}`,
      gap: tokens.spacingHorizontalS,
    },
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  hamburger: {
    display: "none",
    "@media (max-width: 768px)": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "transparent",
      border: "none",
      cursor: "pointer",
      padding: tokens.spacingHorizontalS,
      color: "#fff",
      fontSize: "24px",
      minWidth: "44px",
      minHeight: "44px",
    },
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  userMenuTrigger: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    userSelect: "none",
    color: "#fff",
    "& svg": {
      color: "#fff",
    },
    "&:hover, &:active, &:focus": {
      background: "inherit",
      color: tokens.colorBrandForeground1,
      boxShadow: "none",
      outline: "none",
      "& svg": {
        color: tokens.colorBrandForeground1,
      },
    },
  },
  userName: {
    marginLeft: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase400,
    "@media (max-width: 768px)": {
      display: "none",
    },
  },
  themeSwitch: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    marginRight: "20px",
    "@media (max-width: 768px)": {
      marginRight: "8px",
      "& > svg": {
        display: "none",
      },
    },
  },
  logo: {
    "@media (max-width: 768px)": {
      width: "50px",
      height: "50px",
    },
  },
  userButton: {
    backgroundColor: "transparent",
    border: "none",
    fontSize: "1.05rem",
  },
});

interface HeaderbarProps {
  onMenuClick?: () => void;
}

const Headerbar: React.FC<HeaderbarProps> = ({ onMenuClick }) => {
  const styles = useStyles();
  const { mode } = useConfigStore((state) => state.themeConfig);
  const setThemeMode = useConfigStore((state) => state.setThemeMode);

  const { instance, accounts } = useMsal();
  const user = accounts && accounts[0];
  const accountInfo = accounts[0] || null;
  const dataService = useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const ROLE_STORAGE_KEY = "curriculumUserRole";
  const PARTS_STORAGE_KEY = "curriculumUserParts";

  const fetchAndStoreRole = async (email: string) => {
    try {
      if (!dataService) return;
      const response = await dataService.getUserAppPermissions(email, "3");
      if (response) {
        console.log("fetchAndStoreRole called for:", email);
        const roles = response.map((perm) => perm.rolle);
        const personalParts = response.map((perm) => perm.appPart);
        sessionStorage.setItem(ROLE_STORAGE_KEY, JSON.stringify(roles));
        sessionStorage.setItem(
          PARTS_STORAGE_KEY,
          JSON.stringify(personalParts),
        );
        console.log(
          "Role set in sessionStorage:",
          sessionStorage.getItem(ROLE_STORAGE_KEY),
        );
        window.dispatchEvent(new CustomEvent("role-changed"));
        console.log("role-changed event dispatched");
      } else {
        console.log("No permissions returned from API for user:", email);
      }
    } catch (e) {
      console.error("Failed to fetch user role", e);
    }
  };

  useEffect(() => {
    console.log("Headerbar useEffect - user:", user?.username);
    if (user?.username) {
      const stored = sessionStorage.getItem(ROLE_STORAGE_KEY);
      const parsed = stored ? (JSON.parse(stored) as string[]) : null;
      console.log("Current sessionStorage role:", parsed);

      if (!parsed || parsed.length === 0) {
        console.log("Fetching role for user...");
        fetchAndStoreRole(user.username);
      }
    } else {
      console.log("No user - removing sessionStorage");
      sessionStorage.removeItem(ROLE_STORAGE_KEY);
    }
  }, [user]);

  const handleLogin = async () => {
    instance.loginRedirect();
    const account = instance.getAllAccounts()[0];
    if (account?.username) {
      fetchAndStoreRole(account.username);
    }
  };

  return (
    <header className={styles.header}>
      <div className={styles.container}>
        <div className={styles.leftSection}>
          <button
            className={styles.hamburger}
            onClick={onMenuClick}
            aria-label="Toggle menu"
          >
            <Navigation24Regular />
          </button>
          <Link href="/" style={{ display: "inline-block" }}>
            <Image
              src="/imc.svg"
              alt="Logo"
              width={70}
              height={70}
              className={styles.logo}
              style={{
                margin: "10px",
                cursor: "pointer",
              }}
            />
          </Link>
          <Text as="span" className={styles.title}>
            IMC Programme Development
          </Text>
        </div>
        <div className={styles.actions}>
          <div className={styles.themeSwitch}>
            <WeatherSunnyRegular />
            <Switch
              title="Toggle theme"
              aria-checked={mode === "dark"}
              checked={mode === "dark"}
              onChange={(_, data) =>
                setThemeMode(data.checked ? "dark" : "light")
              }
            />
            <WeatherMoonRegular />
          </div>
          {/* Auth User Info / Login */}
          {user ? (
            <Menu>
              <MenuTrigger>
                <div className={styles.userMenuTrigger} tabIndex={0}>
                  <Person24Regular style={{ fontSize: 28 }} />
                  <Text className={styles.userName}>
                    {user.name || user.username}
                  </Text>
                </div>
              </MenuTrigger>
            </Menu>
          ) : (
            <div
              className={styles.userMenuTrigger}
              tabIndex={0}
              onClick={handleLogin}
              style={{ fontWeight: 500 }}
            >
              <Person24Regular style={{ fontSize: 28 }} />
              <Text className={styles.userName}>Anmelden</Text>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Headerbar;
